import type { TFunction } from 'react-i18next'
import * as yup from 'yup'

import {
  SERVICE_NAME_MAX_LENGTH,
  type CreateInternalServiceProps,
  type EditInternalServiceProps,
  type InternalService
} from '@percent/admin-dashboard/api/actions/internalServices/internalServices.types'

export type ServiceFormValues = {
  name: string
  ownerTeam: string
  description: string
}

export const emptyServiceForm: ServiceFormValues = { name: '', ownerTeam: '', description: '' }

export const serviceFormValuesOf = ({ name, ownerTeam, description }: InternalService): ServiceFormValues => ({
  name,
  ownerTeam,
  description: description ?? ''
})

export const serviceFormSchema = (t: TFunction) =>
  yup.object({
    name: yup
      .string()
      .trim()
      .required(t('errorMessage.required'))
      .max(SERVICE_NAME_MAX_LENGTH, t('errorMessage.shouldNotExceed255')),
    ownerTeam: yup
      .string()
      .trim()
      .required(t('errorMessage.required'))
      .max(100, t('errorMessage.shouldNotExceedCharacterLimit', { limit: 100 })),
    description: yup
      .string()
      .trim()
      .max(1024, t('errorMessage.shouldNotExceedCharacterLimit', { limit: 1024 }))
  })

export const toCreateProps = ({ name, ownerTeam, description }: ServiceFormValues): CreateInternalServiceProps => ({
  name: name.trim(),
  ownerTeam: ownerTeam.trim(),
  ...(description.trim() && { description: description.trim() })
})

/** Only what changed from `service`, with a cleared description as null; null when nothing changed. */
export const toEditProps = (values: ServiceFormValues, service: InternalService): EditInternalServiceProps | null => {
  const name = values.name.trim()
  const ownerTeam = values.ownerTeam.trim()
  const description = values.description.trim() || null
  const changes = {
    ...(name !== service.name && { name }),
    ...(ownerTeam !== service.ownerTeam && { ownerTeam }),
    ...(description !== service.description && { description })
  }

  return Object.keys(changes).length > 0 ? { id: service.id, ...changes } : null
}
